import type { BuyerOrderBy, MutationCreateBuyerArgs, MutationUpdateBuyerArgs, SortOrder } from '@/graphql/graphql';
import { CREATE_BUYER, GET_BUYER, GET_BUYERS, GET_BUYER_SUMMARY, UPDATE_BUYER } from '@/graphql/queries/buyers.graphql';
import { apolloClient } from './apollo-client';

/**
 * Buyers
 */
const buyersSortFieldMap: Record<string, keyof BuyerOrderBy> = {
    sku: 'sku',
    legalName: 'legalName',
    contactName: 'contactName',
    contactEmail: 'contactEmail',
    publicName: 'publicName',
};

export type FetchBuyersListArgs = {
    page: number;
    pageSize: number;
    filters?: {
        [key: string]: unknown;
    };
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    admin?: boolean;
};

export async function fetchBuyersList({
    page,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    admin = false,
}: FetchBuyersListArgs) {
    const sortObj: BuyerOrderBy = {};

    if (sortBy && buyersSortFieldMap[sortBy]) {
        sortObj[buyersSortFieldMap[sortBy]] = sortOrder as SortOrder;
    }

    const response = await apolloClient.query({
        query: GET_BUYERS,
        variables: {
            limit: pageSize,
            offset: (page - 1) * pageSize,
            orderBy: sortObj,
            contactEmail: filters?.contactEmail,
            contactName: filters?.contactName,
            sku: filters?.sku && Number(filters?.sku),
            legalName: filters?.legalName,
            publicName: filters?.publicName,
            asAdmin: admin,
        },
    });

    return { data: response.data.searchBuyers.buyers, total: response.data.searchBuyers.count };
}

export async function updateBuyer(input: MutationUpdateBuyerArgs) {
    const result = await apolloClient.mutate({
        mutation: UPDATE_BUYER,
        variables: {
            billingEmail: input.billingEmail?.toLocaleLowerCase(),
            broadcastLanguage: input.broadcastLanguage,
            broadcastCountries: input.broadcastCountries,
            broadcastType: input.broadcastType,
            businessProviderId: input.businessProviderId,
            contactEmail: input.contactEmail?.toLocaleLowerCase(),
            contactName: input.contactName,
            hourCost: input.hourCost,
            payoutTemplate: input.payoutTemplate,
            id: input.id,
            legalName: input.legalName,
            licensesDuration: input.licensesDuration,
            postalAddress: input.postalAddress,
            publicName: input.publicName,
            replayRights: input.replayRights,
            vatId: input.vatId,
            videoOutputBitrate: input.videoOutputBitrate,
            videoOutputFormat: input.videoOutputFormat,
            cognitoPassword: input.cognitoPassword,
        },
    });

    return { data: result };
}

export async function createBuyer(input: MutationCreateBuyerArgs) {
    const result = await apolloClient.mutate({
        mutation: CREATE_BUYER,
        variables: {
            billingEmail: input.billingEmail?.toLocaleLowerCase(),
            broadcastLanguage: input.broadcastLanguage,
            broadcastCountries: input.broadcastCountries,
            broadcastType: input.broadcastType,
            businessProviderId: input.businessProviderId,
            contactEmail: input.contactEmail?.toLocaleLowerCase(),
            contactName: input.contactName,
            hourCost: input.hourCost as number,
            payoutTemplate: input.payoutTemplate,
            legalName: input.legalName,
            licensesDuration: input.licensesDuration,
            postalAddress: input.postalAddress,
            publicName: input.publicName,
            replayRights: input.replayRights,
            vatId: input.vatId,
            videoOutputBitrate: input.videoOutputBitrate,
            videoOutputFormat: input.videoOutputFormat,
        },
    });

    return { data: result };
}

export async function getBuyer(id: string, full = false) {
    const response = await apolloClient.query({
        query: full ? GET_BUYER : GET_BUYER_SUMMARY,
        variables: {
            id: id,
        },
    });

    return response.data.buyer;
}
